'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import {
  useLearningSource,
  useBlogSource,
  useMediaSource,
  type LearningCategory,
  type BlogCategory,
  type MediaCategory,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';

/**
 * Auto-scrolling carousel of category tiles. Driven by one of several
 * category sources (courses / blog / video / custom). Defaults to
 * {@code courses} for parity with the legacy block.
 */
export type CategoryFeedSource = 'courses' | 'blog' | 'video' | 'custom';

export interface CategoryItem {
  id?: string | number;
  slug?: string;
  name: string;
  image?: string;
  description?: string;
}

export interface CategoryCarouselProps {
  title?: string;
  feedSource?: CategoryFeedSource;
  limit?: number;
  speed?: 'slow' | 'medium' | 'fast';
  aspect?: 'portrait' | 'square' | 'landscape';
  autoplay?: boolean;
  pauseOnHover?: boolean;
  direction?: 'left' | 'right';
  hrefTemplate?: string;
  /** Operator-supplied items when {@code feedSource === 'custom'}. */
  items?: CategoryItem[];
}

const SPEED_DURATION: Record<string, string> = { slow: '60s', medium: '40s', fast: '25s' };
const ASPECT: Record<string, string> = {
  portrait: '3 / 4',
  square: '1 / 1',
  landscape: '16 / 9',
};

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function CategoryCarousel({
  title,
  feedSource = 'courses',
  limit: rawLimit,
  speed = 'medium',
  aspect = 'landscape',
  pauseOnHover = true,
  direction = 'left',
  hrefTemplate,
  items: customItems,
}: CategoryCarouselProps) {
  const { t } = useTranslation('site-builder-blocks');
  const learning = useLearningSource();
  const blog = useBlogSource();
  const media = useMediaSource();
  const limit = clamp(
    typeof rawLimit === 'number' && Number.isFinite(rawLimit) ? rawLimit : 20,
    4,
    48,
  );

  const state = useAsyncResource<CategoryItem[]>(async () => {
    if (feedSource === 'custom') {
      return customItems && customItems.length > 0 ? customItems.slice(0, limit) : null;
    }
    if (feedSource === 'courses') {
      if (!learning) return null;
      const cats = await learning.fetchCategories();
      return cats.length > 0 ? cats.slice(0, limit).map(toItemFromLearning) : null;
    }
    if (feedSource === 'blog') {
      if (!blog) return null;
      const cats = await blog.listCategories();
      return cats.length > 0 ? cats.slice(0, limit).map(toItemFromBlog) : null;
    }
    if (feedSource === 'video') {
      if (!media) return null;
      const cats = await media.categories.list();
      return cats.length > 0 ? cats.slice(0, limit).map(toItemFromMedia) : null;
    }
    return null;
  }, [feedSource, learning, blog, media, customItems, limit]);

  const adapterMissing =
    (feedSource === 'courses' && !learning) ||
    (feedSource === 'blog' && !blog) ||
    (feedSource === 'video' && !media);

  if (adapterMissing) {
    return (
      <SectionShell title={title}>
        <AdapterPlaceholder
          blockType="CategoryCarousel"
          adapterName={
            feedSource === 'courses'
              ? 'LearningSource'
              : feedSource === 'blog'
                ? 'BlogSource'
                : 'MediaSource'
          }
        />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={title}>
        <SkeletonRow count={limit} layout="rail" minCardWidth={180} />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={title}>
        <EmptyState
          title={t('shared.empty.categories.title')}
          hint={t('shared.empty.categories.hint')}
        />
      </SectionShell>
    );
  }

  const duration = SPEED_DURATION[speed] ?? '40s';
  const aspectRatio = ASPECT[aspect] ?? '16 / 9';
  const animDirection = direction === 'right' ? 'reverse' : 'normal';

  return (
    <SectionShell title={title}>
      <style dangerouslySetInnerHTML={{ __html: CAROUSEL_CSS }} />
      <div
        className={`sb-category-carousel ${pauseOnHover ? 'sb-pause-on-hover' : ''}`}
        style={
          {
            '--duration': duration,
            '--direction': animDirection,
          } as CSSProperties
        }
      >
        <div className="sb-category-track">
          {[...state.data, ...state.data].map((item, i) => (
            <CategoryTile
              key={`${item.id ?? item.slug ?? item.name}-${i}`}
              item={item}
              aspectRatio={aspectRatio}
              hrefTemplate={hrefTemplate ?? defaultHrefTemplate(feedSource)}
            />
          ))}
        </div>
      </div>
    </SectionShell>
  );
}

function defaultHrefTemplate(source: CategoryFeedSource): string {
  if (source === 'blog') return '/blog/explore?category={slug}';
  if (source === 'video') return '/tube/category/{slug}';
  return '/categories/{slug}';
}

function toItemFromLearning(c: LearningCategory): CategoryItem {
  const item: CategoryItem = { id: c.id, name: c.name };
  if (c.slug !== undefined) item.slug = c.slug;
  if (c.description !== undefined) item.description = c.description;
  if (c.imageUrl !== undefined) item.image = c.imageUrl;
  return item;
}

function toItemFromBlog(c: BlogCategory): CategoryItem {
  const item: CategoryItem = { id: c.id, slug: c.slug, name: c.name };
  if (c.description !== undefined) item.description = c.description;
  if (c.image !== undefined) item.image = c.image;
  return item;
}

function toItemFromMedia(c: MediaCategory): CategoryItem {
  const item: CategoryItem = { id: c.slug, slug: c.slug, name: c.name };
  if (c.description !== undefined) item.description = c.description;
  return item;
}

function CategoryTile({
  item,
  aspectRatio,
  hrefTemplate,
}: {
  item: CategoryItem;
  aspectRatio: string;
  hrefTemplate: string;
}) {
  const key = String(item.slug ?? item.id ?? item.name);
  const href = hrefTemplate.replace('{slug}', encodeURIComponent(key));
  return (
    <Link
      href={href}
      style={{
        flex: '0 0 220px',
        textDecoration: 'none',
        color: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div
        aria-hidden
        style={{
          aspectRatio,
          borderRadius: 'var(--radius-md, 8px)',
          background: item.image
            ? `url(${item.image}) center/cover no-repeat`
            : 'var(--color-neutral-200, #e4e4e7)',
        }}
      />
      <div style={{ fontSize: 'var(--font-size-sm, 14px)', fontWeight: 600, lineHeight: 1.3 }}>
        {item.name}
      </div>
    </Link>
  );
}

const CAROUSEL_CSS = `
  .sb-category-carousel {
    overflow: hidden;
    position: relative;
  }
  .sb-category-track {
    display: flex;
    gap: 16px;
    width: max-content;
    animation: sb-category-scroll var(--duration, 40s) linear infinite;
    animation-direction: var(--direction, normal);
  }
  .sb-pause-on-hover:hover .sb-category-track {
    animation-play-state: paused;
  }
  @keyframes sb-category-scroll {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  @media (prefers-reduced-motion: reduce) {
    .sb-category-track { animation: none; }
  }
`;
